import React, { useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import qz from 'qz-tray';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { ShowSuccessFetch, showError, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import { formatDate } from 'src/utils/string/stringUtil';
import {
    deleteHRM1060_R01, downloadExcel_HRM1060_R01, getHRM1060_R01AllCount,
    getHRM1060_R01List, getPdfBlob_HRM1060_R01, patchHRM1060_R01,
    patchHRM1060_R01Print, postHRM1060_R01,
} from './Api_HRM1060_R01';
import { defaultHRM1060_R01Dto, HRM1060_R01Dto } from './Dto_HRM1060_R01';
import { HRM1060_R01Filter, useHRM1060_R01Store } from './Store_HRM1060_R01';
import { CERT_STS_CANCEL, CERT_STS_REQUEST, isHRM1060_R01Printed, MAX_COPIES, MIN_COPIES } from './Util_HRM1060_R01';

/** 조회·출력 후 재조회 동작을 제어하는 옵션이다. */
interface FetchOptions {
    messageMode?: 'show' | 'none'; // none이면 조회 알림을 생략
    keepCertNo?: string; // 재조회 뒤 다시 선택할 발급번호
}

/** 상위 Container가 넘기는 그리드와 메시지 참조이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1060_R01Dto> | null>; // 셀 편집 확정용 ref
    setMessage: (message: string) => void; // 화면 하단 메시지 설정
}

/** 조회·저장·삭제·출력·Excel 업무 함수를 화면 버튼에 제공한다. @param props 그리드와 메시지 @returns 버튼 동작 */
export const Content_HRM1060_R01 = ({ gridRef, setMessage }: Props) => {
    const {
        HRM1060_R01Filter, setHRM1060_R01PastFilter,
        HRM1060_R01List, setHRM1060_R01List, insertHRM1060_R01List,
        removeHRM1060_R01List, selectedHRM1060_R01List, removeSelectedHRM1060_R01List,
        deletedHRM1060_R01List, addDeletedHRM1060_R01List, removeDeletedHRM1060_R01List,
        setOriginalHRM1060_R01List, setAllCount, setScrollTop, initData,
    } = useHRM1060_R01Store(); // 화면 저장 상태와 변경 함수
    const { certTypeList } = useCommonCodeStore(); // 증명서 종류 검증용 공통코드
    const { setLoadingSpinner } = useLoadingSpinnerStore(); // 비동기 요청 로딩 표시
    const { printPdfFromBlob } = useQzPrint(); // QZ PDF 출력 함수
    useFetchActionbarAuth('HRM1060_R01'); // 화면 권한 로드

    /** 화면 표시용 empNm을 제외하고 빈 검색값을 요청에서 뺀다. @param filter 화면 조건 @returns API 조건 */
    const getParams = useCallback((filter: HRM1060_R01Filter): Partial<HRM1060_R01Filter> => ({
        issueYear: filter.issueYear || undefined,
        certType: filter.certType || undefined,
        issueSts: filter.issueSts || undefined,
        keyword: filter.keyword || undefined,
    }), []);

    /** 현재 조건의 전체 건수를 갱신한다. @returns 서버 건수 */
    const renewalAllCount = useCallback(async () => {
        const response = await getHRM1060_R01AllCount(getParams(HRM1060_R01Filter)); // 건수 API 응답
        if (!response.data.successFlag) return 0;
        setAllCount(response.data.data);
        return response.data.data;
    }, [getParams, HRM1060_R01Filter, setAllCount]);

    /** 목록과 건수를 재조회하고 지정 번호가 있으면 다시 선택한다. @param fetchType 조회 종류 @param options 메시지·선택 보존 @returns 조회 완료 */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: FetchOptions) => {
        setLoadingSpinner(true);
        try {
            if (fetchType === 'search') setScrollTop(null);
            const response = await getHRM1060_R01List(fetchType, getParams(HRM1060_R01Filter), 0); // 대장 응답
            if (!response.data.successFlag) return;
            const list = response.data.data; // 조회 결과
            setHRM1060_R01List(list);
            setOriginalHRM1060_R01List(list);
            useHRM1060_R01Store.getState().initSelectedHRM1060_R01List();
            removeDeletedHRM1060_R01List();
            if (options?.keepCertNo) {
                const index = list.findIndex(row => row.certNo === options.keepCertNo); // 출력 후 유지할 행 위치
                if (index >= 0) useHRM1060_R01Store.getState().addSelectedHRM1060_R01List([String(index + 1)]);
            }
            await renewalAllCount();
            if (list.length > 0) gridRef.current?.api.ensureIndexVisible(0, 'top');
            if (options?.messageMode !== 'none') ShowSuccessFetch(setMessage, `조회되었습니다. ${list.length}건`);
            setHRM1060_R01PastFilter(HRM1060_R01Filter);
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, setScrollTop, getParams, HRM1060_R01Filter, setHRM1060_R01List,
        setOriginalHRM1060_R01List, removeDeletedHRM1060_R01List, renewalAllCount,
        gridRef, setMessage, setHRM1060_R01PastFilter]);

    /** 조회 버튼과 검색 입력 Enter에서 실행한다. @returns 조회 완료 */
    const fetchData = useCallback(() => executeDataFetch('search'), [executeDataFetch]);

    /** 신규 행은 오늘 날짜·1매·신청 상태로 만든다. @returns 신규 행 */
    const defaultHRM1060_R01Row = useCallback((): HRM1060_R01Dto => ({
        ...defaultHRM1060_R01Dto, // 공통 발급 행 기본값을 이어받는다
        issueDt: formatDate(new Date()),
        copies: 1,
        issueSts: CERT_STS_REQUEST,
        inserted: true,
    }), []);

    /** 화면 상태와 안내 메시지를 초기화한다. */
    const initContainerData = useCallback(() => {
        initData();
        setMessage('');
    }, [initData, setMessage]);

    /** 등록 버튼 또는 행 +에서 신규 행을 삽입한다. @param event 클릭 이벤트 @param rowIndex 기준 행 순서 @param setClickable 행 클릭 무시 함수 */
    const handleInsertClick = useCallback((
        event: React.MouseEvent,
        rowIndex?: number,
        setClickable?: (clickable: boolean) => void,
    ) => {
        const isGridInsert = rowIndex !== undefined; // true이면 행 번호의 + 버튼
        InsertRowHandler({
            event, dataList: HRM1060_R01List,
            rowData: isGridInsert ? HRM1060_R01List[rowIndex] : { ...defaultHRM1060_R01Row(), valueIndex: -1 },
            insertRow: insertHRM1060_R01List,
            defaultRowData: defaultHRM1060_R01Row(),
            ...(setClickable && { setClickable }), // 행 + 직후 이어지는 클릭을 한 번 무시
        });
    }, [HRM1060_R01List, defaultHRM1060_R01Row, insertHRM1060_R01List]);

    /** 프론트에서 확실한 필수값과 길이·매수를 검사한다. 재직상태 허용 여부는 서버 ref01로 판단한다.
     * @param row 저장할 신규·수정 행 @returns true이면 화면 검증 통과
     */
    const validateRow = useCallback((row: HRM1060_R01Dto): boolean => {
        if (!row.empNo) {
            showWarning('사원을 선택해주세요.');
            return false;
        }
        if (!certTypeList.some(code => code.minorCd === row.certType)) {
            showWarning('증명서를 선택해주세요.');
            return false;
        }
        if (!row.purpose.trim()) {
            showWarning('용도를 입력해주세요.');
            return false;
        }
        if (row.purpose.trim().length > 100) {
            showWarning('용도는 100자까지 입력할 수 있습니다.');
            return false;
        }
        // ??: submit_to는 DB에서 NULL을 허용하므로 조회 값이 null이면 빈 값으로 보고 길이를 잰다.
        if ((row.submitTo ?? '').length > 100) {
            showWarning('제출처는 100자까지 입력할 수 있습니다.');
            return false;
        }
        if (!Number.isInteger(row.copies) || row.copies < MIN_COPIES || row.copies > MAX_COPIES) {
            showWarning('매수는 1~10장 사이로 입력해주세요.');
            return false;
        }
        if (!row.issueDt) {
            showWarning('발급일자를 입력해주세요.');
            return false;
        }
        return true;
    }, [certTypeList]);

    /** 신규·수정·삭제를 나눠 전송하고 부분 성공도 재조회한다. @returns 저장 완료 */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        const state = useHRM1060_R01Store.getState(); // 마지막 셀 편집까지 반영된 Store
        const currentList = state.HRM1060_R01List; // 실제 저장 대상 목록
        const deletedSet = new Set(state.deletedHRM1060_R01List); // 삭제 표시 식별값
        /** 삭제 표시가 있는 행인지 확인한다. @param row 검사 행 @returns true이면 삭제 표시 */
        const isDeleted = (row: HRM1060_R01Dto) => deletedSet.has(String(row.valueIndex));
        /** 완전히 비어 있는 신규 입력 행인지 확인한다. @param row 검사 행 @returns true이면 저장 제외 */
        const isBlank = (row: HRM1060_R01Dto) =>
            row.inserted && !row.empNo && !row.certType && !row.purpose.trim();
        const activeRows = currentList.filter(row => !isDeleted(row) && !isBlank(row)); // 저장 또는 유지할 행
        const insertedRows = activeRows.filter(row => row.inserted); // 신규 요청
        const modifiedRows = activeRows.filter(row => row.isModified && !row.inserted); // 수정 요청
        const deletedRows = currentList.filter(row => isDeleted(row) && !row.inserted); // 삭제 요청
        if (insertedRows.length + modifiedRows.length + deletedRows.length === 0) {
            showWarning('저장할 내용이 없습니다.'); return;
        }
        for (const row of [...insertedRows, ...modifiedRows]) {
            if (!validateRow(row)) return;
        }
        const responses = await Promise.all([
            insertedRows.length
                ? postHRM1060_R01(insertedRows) : Promise.resolve(undefined),
            modifiedRows.length
                ? patchHRM1060_R01(modifiedRows) : Promise.resolve(undefined),
            deletedRows.length
                ? deleteHRM1060_R01(deletedRows.map(row => row.certNo).join(','))
                : Promise.resolve(undefined),
        ]); // 신규·수정·삭제를 각각 독립적인 API로 전송한다
        const sent = responses.filter(
            (response): response is NonNullable<typeof response> => response !== undefined,
        ); // 실제 전송한 응답
        if (!sent.every(response => response.data.successFlag)) {
            if (sent.some(response => response.data.successFlag)) {
                await executeDataFetch('refetch', { messageMode: 'none' });
            }
            return;
        }
        showSuccess('증명서 저장이 완료되었습니다.');
        setMessage('증명서 저장이 완료되었습니다.');
        await executeDataFetch('refetch', { messageMode: 'none' });
    }, [gridRef, validateRow, executeDataFetch, setMessage]);

    /** 선택한 출력 전 행만 삭제 표시한다. 이미 표시된 경우 복구한다. */
    const handleDeleteClick = useCallback(() => {
        if (deletedHRM1060_R01List.length) {
            removeDeletedHRM1060_R01List();
            removeSelectedHRM1060_R01List();
            return;
        }
        if (!selectedHRM1060_R01List.length) return;
        const selectedSet = new Set(selectedHRM1060_R01List); // 선택 식별값
        const chosen = HRM1060_R01List.filter(row => selectedSet.has(String(row.valueIndex))); // 실제 선택 행
        if (chosen.some(isHRM1060_R01Printed)) {
            showWarning('출력한 증명서는 취소로 처리하세요.');
            return;
        }
        chosen.filter(row => row.inserted).forEach(row => removeHRM1060_R01List(row.valueIndex));
        const existingIds = chosen.filter(row => !row.inserted).map(row => String(row.valueIndex)); // 서버 삭제 대상
        if (existingIds.length) addDeletedHRM1060_R01List(existingIds);
        removeSelectedHRM1060_R01List();
    }, [deletedHRM1060_R01List, selectedHRM1060_R01List, HRM1060_R01List,
        removeDeletedHRM1060_R01List, removeSelectedHRM1060_R01List,
        removeHRM1060_R01List, addDeletedHRM1060_R01List]);

    /** 현재 검색 조건의 Excel을 내려받는다. @returns 다운로드 완료 */
    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM1060_R01('search', getParams(HRM1060_R01Filter), 0); // XLSX Base64 응답
            if (!response.data.successFlag) return;
            downloadSettingExcel({ responseData: response.data.data, fileName: '증명서 발급-excel.xlsx' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, getParams, HRM1060_R01Filter]);

    /** 한 행의 출력 기록을 먼저 저장한 뒤 PDF를 QZ 또는 브라우저로 인쇄한다. @returns 출력과 재조회 완료 */
    const handlePrintClick = useCallback(async () => {
        const state = useHRM1060_R01Store.getState(); // 현재 선택과 편집 상태
        if (state.selectedHRM1060_R01List.length !== 1) {
            showWarning('출력할 증명서를 선택하세요.'); return;
        }
        const row = state.HRM1060_R01List.find(item =>
            String(item.valueIndex) === state.selectedHRM1060_R01List[0]); // 출력 대상
        if (!row) {
            showWarning('출력할 증명서를 선택하세요.');
            return;
        }
        if (row.inserted || row.isModified) {
            showWarning('저장 후 출력하세요.');
            return;
        }
        if (row.issueSts === CERT_STS_CANCEL) {
            showWarning('취소된 증명서는 출력할 수 없습니다.');
            return;
        }
        setLoadingSpinner(true);
        let isPrintRecorded = false; // true이면 서버에 출력횟수·발급 상태가 이미 기록됨
        try {
            const result = await patchHRM1060_R01Print(row.certNo); // 매수와 관계없이 한 번 기록
            if (!result.data.successFlag) return;
            isPrintRecorded = true;
            const blob = await getPdfBlob_HRM1060_R01(row.certNo); // 기록된 출력횟수를 포함한 PDF
            if (qz.websocket.isActive()) {
                const ok = await printPdfFromBlob(blob, 'report1'); // QZ 인쇄 결과
                if (!ok) {
                    const url = URL.createObjectURL(blob); // 브라우저 폴백 URL
                    const win = window.open(url, '_blank'); // 인쇄할 새 탭
                    if (win) win.addEventListener('load', () => win.print());
                    showWarning('QZ로 출력할 수 없어 브라우저 인쇄 창을 띄웁니다.');
                } else {
                    showSuccess('PDF 출력을 완료하였습니다.');
                }
            } else {
                const url = URL.createObjectURL(blob); // 미연결 시 PDF 새 탭
                window.open(url, '_blank');
                showSuccess('PDF를 새 탭에서 열었습니다.');
            }
        } catch (error) {
            showError('PDF 출력 중 오류가 발생했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
        // PDF 받기나 인쇄가 실패해도 출력 기록이 저장됐으면 재조회해 출력 후 잠금 상태를 화면에 맞춘다.
        if (isPrintRecorded) {
            await executeDataFetch('refetch', { messageMode: 'none', keepCertNo: row.certNo });
        }
    }, [setLoadingSpinner, printPdfFromBlob, executeDataFetch]);

    // 진입·초기화·0건 조회 때 빈 행 하나만 넣는다. 자동 조회 요청은 보내지 않는다.
    useEffect(() => {
        if (HRM1060_R01List.length === 0) handleInsertClick({} as React.MouseEvent);
    }, [HRM1060_R01List, handleInsertClick]);

    return {
        executeDataFetch, fetchData, defaultHRM1060_R01Row, initContainerData,
        handleInsertClick, handleSaveClick, handleDeleteClick, handleExcelClick, handlePrintClick,
    }; // Container가 연결할 화면 동작
};
